import { Check, Minus } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useSilver } from "@/context/SilverContext";
import { SIGNALS, stageLabel } from "@/data/mosaic";
import { fmtInt } from "@/components/silver/common/AnimatedNumber";

function Attr({ label, value, yes }) {
  return (
    <div className="flex items-center justify-between border-b border-white/[0.07] py-3.5 text-[14px]">
      <span className="text-white/60">{label}</span>
      <span className="flex items-center gap-2 font-semibold text-white">
        {yes !== undefined && (yes ? <Check className="h-4 w-4 text-[#C6EE5A]" /> : <Minus className="h-4 w-4 text-white/30" />)}
        {value}
      </span>
    </div>
  );
}

export function ProfileDrawer({ code, onClose }) {
  const { audience } = useSilver();
  const g = audience.mosaic.find((m) => m.code === code);
  const max = g ? Math.max(1, ...g.byDistrict.map((d) => d.households)) : 1;

  return (
    <Sheet open={!!g} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col !rounded-none !rounded-l-[28px] border-l border-white/10 !bg-[rgba(12,30,16,0.94)] p-0 text-white backdrop-blur-2xl sm:max-w-[460px]" data-testid="profile-drawer">
        {g && (
          <>
            <SheetHeader className="px-7 pb-4 pt-8 text-left">
              <p className="eyebrow">Audience profile · {g.code}</p>
              <SheetTitle className="text-3xl font-extrabold tracking-[-0.04em] text-white">{g.name}</SheetTitle>
              <SheetDescription className="text-[14px] text-white/55">Characteristics held in the audience dataset for this profile.</SheetDescription>
            </SheetHeader>
            <div className="thin-scroll flex-1 overflow-y-auto px-7 pb-8">
              <div className="glass mt-2 p-5">
                <p className="eyebrow-muted">In your selected areas</p>
                <p className="num mt-2 text-4xl font-extrabold tracking-[-0.04em] text-[#C6EE5A]" data-testid="profile-drawer-households">{fmtInt(g.households)}</p>
                <p className="text-[13px] text-white/55">households · {Math.round(g.share * 100)}% of your audience</p>
              </div>
              <div className="mt-6">
                <Attr label="Retirement stage" value={stageLabel(g.retirement)} />
                <Attr label="Typical property" value={`${g.bedrooms} bedroom`} />
                {SIGNALS.map((s) => (
                  <Attr key={s.key} label={s.label} value={g[s.key] ? "Yes" : "No"} yes={g[s.key]} />
                ))}
              </div>
              <div className="mt-8">
                <p className="eyebrow-muted">By district</p>
                <ul className="mt-4 space-y-3">
                  {g.byDistrict.map((d) => (
                    <li key={d.code} className="grid grid-cols-[64px_1fr_auto] items-center gap-3 text-[13px]">
                      <span className="font-ui font-semibold text-white">{d.code}</span>
                      <span className="h-1.5 overflow-hidden rounded-full bg-white/10"><span className="block h-full bg-[#96C11F]" style={{ width: `${(d.households / max) * 100}%` }} /></span>
                      <span className="num text-white/70">{fmtInt(d.households)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="mt-8 rounded-2xl border border-dashed border-white/15 p-4 text-[12px] leading-relaxed text-white/45">
                Richer lifestyle insight for this profile is planned as future enriched content. Profiles describe audience characteristics, not the intentions of any individual household.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
